import { Button } from "components/Button";
import { useDomainHandler } from "hooks/useDomainHandler";
import useOnLocationChange from "hooks/useOnLocationChange";
import jwtDecode from "jwt-decode";
import queryString from "query-string";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Heading1, Heading3 } from "styles/elements/Headings";
import { Box } from "styles/helpers/layout";

import { Input } from "components/Input";
import { useRegistration } from "hooks/useRegistration";
import { ParagraphLarge } from "styles/elements/Texts";
import {
  StyledButton,
  StyledContainer,
  StyledEmailHighlight,
  StyledHeader,
  StyledOptionsCard,
  StyledPageLayout,
} from "./OAuthAccountLinkPage.styled";

export const OAuthAccountLinkPage: React.FC = () => {
  const [userHasAccount, setUserHasAccount] = useState(false);
  const [ltiLinkValidated, setLtiLinkValidated] = useState(false);
  const [email, setEmail] = useState("");
  const [existingAccountEmail, setExistingAccountEmail] = useState("");
  const { registerLtiAccount, requestLtiAccount } = useRegistration();
  const history = useHistory();
  const { t } = useTranslation("auth-pages");
  const { getCustomNameByDomain } = useDomainHandler();
  const { queryParams } = useOnLocationChange(history);
  const token = queryString.parse(queryParams);

  const showFormAccount = () => {
    setUserHasAccount(!userHasAccount);
  };

  const registerLTI = ({ code }: { code: string }) => {
    registerLtiAccount(code as string);
    setLtiLinkValidated(false);
  };

  const linkAccountToLti = ({ code, email }: { code: string; email: string }) => {
    requestLtiAccount(code as string, email);
    setLtiLinkValidated(true);
  };

  useEffect(() => {
    if (token?.code) {
      try {
        const tokenInfo: { email: string } = jwtDecode(token.code as string);
        setEmail(tokenInfo.email);
      } catch (_error) {
        setEmail("");
      }
    }
  }, [queryParams, token]);
  return (
    <StyledPageLayout>
      <StyledContainer>
        <StyledHeader>
          <Heading1>{t("oauthLink.title", "Hey, haven't we met before?")}</Heading1>
        </StyledHeader>
        <Box>
          {!userHasAccount ? (
            <Box>
              <Heading3>
                {t("oauthLink.subtitle", "Hello ")}
                <StyledEmailHighlight data-cy="user-email">{email}</StyledEmailHighlight>
              </Heading3>
              <ParagraphLarge marginTop={2}>
                {t(
                  "ltiAccess.details.loginBeforeMessage",
                  `Did you login before on ${getCustomNameByDomain()} with a different e-mail?`,
                  { domainName: getCustomNameByDomain() },
                )}
              </ParagraphLarge>
              <StyledOptionsCard>
                <Button dataCy="show-access-form" variant="primary" onClick={showFormAccount}>
                  {t("ltiAccess.button.accountExist", "I already have an existing account")}
                </Button>
                <Button
                  dataCy="button-continue-lti"
                  variant="primary"
                  onClick={() => registerLTI({ code: token.code as string })}
                >
                  {t("ltiAccess.button.continue", "Please continue")}
                </Button>
              </StyledOptionsCard>
            </Box>
          ) : !ltiLinkValidated ? (
            <Box>
              <ParagraphLarge>
                {t(
                  "ltiAccess.access.message",
                  `You have indicated that you already have a ${getCustomNameByDomain()} account at another e-mail address. Fill in the e-mail address below on which you already have a ${getCustomNameByDomain()} account, then we will make sure that your courses and study progress are saved.`,
                  { domainName: getCustomNameByDomain() },
                )}
              </ParagraphLarge>
              <StyledOptionsCard data-cy="access-form">
                <Box mb={2}>
                  {t(
                    "ltiAccess.access.subtitle",
                    "What was your email address when you logged in before?:",
                  )}
                </Box>
                <Box>
                  <Input
                    placeholder={t("ltiAccess.access.emailPlaceholder", "Enter your email address")}
                    type="email"
                    dataCy="email-lti"
                    value={existingAccountEmail}
                    onChange={(e) => setExistingAccountEmail(e.target.value)}
                  />
                  <StyledButton
                    data-cy="btn-form-lti"
                    variant="primary"
                    onClick={() =>
                      linkAccountToLti({ code: token.code as string, email: existingAccountEmail })
                    }
                  >
                    {t("ltiAccess.access.submit", "Submit")}
                  </StyledButton>
                </Box>
                <Box mt={2} display="flex" alignItems="center">
                  <ParagraphLarge>
                    {t("ltiAccess.details.actualEmail", `${email} is my actual e-mail `, { email })}
                    <Button
                      style={{ cursor: "pointer" }}
                      data-cy="skip-lti-link"
                      onClick={() => registerLTI({ code: token.code as string })}
                    >
                      {t("ltiAccess.button.skip", "please skip")}
                    </Button>
                  </ParagraphLarge>
                </Box>
              </StyledOptionsCard>
            </Box>
          ) : (
            <Box data-cy="success-lti-link">
              <ParagraphLarge>
                {t(
                  "ltiAccess.details.successEmail",
                  "You have received an email at this address to confirm the linking of your accounts.",
                )}
              </ParagraphLarge>
            </Box>
          )}
        </Box>
      </StyledContainer>
    </StyledPageLayout>
  );
};
